import { useEffect, useState } from "react";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { ProLayout, PageContainer } from "@ant-design/pro-components";
import {
  App,
  Avatar,
  Badge,
  Breadcrumb,
  ConfigProvider,
  Button,
  Dropdown,
  Result,
  Space,
  Spin,
} from "antd";
import {
  AppstoreOutlined,
  BellOutlined,
  HomeOutlined,
  FileDoneOutlined,
  QuestionCircleOutlined,
  LogoutOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  DownOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  SoundOutlined,
  SettingOutlined,
  TeamOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { api, post, session } from "./api";
import type { Agent, Task } from "./domain";
import { Login, PasswordForm } from "./auth";
import { TaskDetail, Tasks } from "./tasks";
import { TaskEditor } from "./task-editor";
import { Reports } from "./reports";
import { FinancePage } from "./finance";
import { Advertising, AdvertisingCreate } from "./advertising";
import { AdvertisingMaterials } from "./advertising-materials";
import { AdvertisingReports } from "./advertising-reports";
import { Dashboard } from "./dashboard";
import { MaterialsPage } from "./materials";
import { SubmissionsCenter } from "./submissions";
import { HelpCenter } from "./help";
import { FeedbackCreate, FeedbackDetail, FeedbackList } from "./feedback";
import { useNotificationSummary } from "./notifications-state";
import { selectedNavigation, businessNavigation } from "./navigation";
import { AdvertisingAccess, hasAdvertisingAccess } from "./advertising-access";
import { Account, Notifications } from "./workspace";
import { HeaderTools } from "./header-tools";
import { pageTab } from "./navigation";
import { PageTabs } from "./page-tabs";
import { BusinessAccess } from "./business-access";
import { businessFromPath, businessLabels, defaultBusinessPath, firstTaskBusiness, hasBusinessAccess, taskBasePath, taskRouteBusiness, taskBusinesses, taskPath } from "./business";

function WorkspaceBrand() {
  return (
    <Link to="/" className="layout-logo" aria-label="体验官商户平台首页">
      <img className="brand-mark small" src="/brand-icon.png" alt="体验官 Logo" />
      <span><h1>体验官 · 商户平台</h1></span>
    </Link>
  );
}

export function AgentApp() {
  const [fontSize, setFontSize] = useState(14);
  const [agent, setAgent] = useState<Agent | null>(null);
  const [checking, setChecking] = useState(!!session.get());
  const [error, setError] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const notifications = useNotificationSummary(agent && !agent.must_change_password ? agent.id : undefined);
  const load = async (background = false) => {
    const token = session.get();
    if (!token) return;
    if (!background) setChecking(true);
    setError("");
    try {
      const principal = await api<Agent>("/auth/me");
      if (session.get() === token) setAgent(principal);
    } catch (e) {
      if (session.get() === token) setError((e as Error).message);
    } finally {
      setChecking(false);
    }
  };
  useEffect(() => {
    if (session.get()) void load();
    const expired = () => {
      setAgent(null);
      setError("");
      message.warning("登录已失效，请重新登录");
    };
    const forced = () =>
      setAgent((a) => (a ? { ...a, must_change_password: true } : a));
    const refresh = () => { if (document.visibilityState !== "hidden" && session.get()) void load(true); };
    const advertisingDenied = () => setAgent((a) => a ? { ...a, advertising_enabled: false } : a);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("agent:advertising-denied", advertisingDenied);
    window.addEventListener("agent:unauthorized", expired);
    window.addEventListener("agent:password-required", forced);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("agent:advertising-denied", advertisingDenied);
      window.removeEventListener("agent:unauthorized", expired);
      window.removeEventListener("agent:password-required", forced);
    };
  }, []);
  const logout = async () => {
    try {
      await post("/auth/logout");
    } catch {
      /* Clear local credential even if remote service is unavailable. */
    }
    session.clear();
    setAgent(null);
    navigate("/");
  };
  if (checking)
    return (
      <div className="app-loading">
        <Spin size="large" />
        <p>正在安全连接商户平台…</p>
      </div>
    );
  if (error)
    return (
      <Result
        status="error"
        title="暂时无法连接商户平台"
        subTitle={error}
        extra={
          <Space>
            <Button type="primary" onClick={() => void load()}>
              重新连接
            </Button>
            <Button
              onClick={() => {
                session.clear();
                setError("");
                setAgent(null);
              }}
            >
              返回登录
            </Button>
          </Space>
        }
      />
    );
  if (!agent)
    return (
      <Login
        onLogin={(a) => {
          setAgent(a);
          navigate("/");
        }}
      />
    );
  if (agent.must_change_password)
    return (
      <PasswordForm
        forced
        onDone={() => {
          setAgent(null);
          navigate("/");
        }}
      />
    );
  const routes = [
    { path: "/", key: "/", name: "首页", icon: <HomeOutlined /> },
    ...taskBusinesses.filter((business) => hasBusinessAccess(agent, business)).map((business) => ({
      path: `/${business}`, key: business, name: businessLabels[business], icon: business === "sms" ? <FileDoneOutlined /> : <AppstoreOutlined />, routes: businessNavigation(business),
    })),
    ...(hasAdvertisingAccess(agent) ? [{ path: "/advertising", key: "advertising", name: "发布广告", icon: <SoundOutlined />, routes: [
      { path: "/advertising/new", key: "advertising-new", name: "新建广告" },
      { path: "/advertising", key: "advertising-list", name: "我的广告" },
      { path: "/advertising/reports", key: "advertising-reports", name: "广告报表" },
      { path: "/advertising/materials", key: "advertising-materials", name: "广告素材" },
    ] }] : []),
    { path: "/finance", key: "/finance", name: "资金与结算", icon: <WalletOutlined /> },
    { path: "/notifications", key: "/notifications", name: "消息中心", icon: <BellOutlined /> },
    { path: "/account", key: "/account", name: "账号与安全", icon: <SettingOutlined /> },
    { path: "/help", key: "/help", name: "帮助与反馈", icon: <QuestionCircleOutlined /> },
  ];
  const currentBusiness = businessFromPath(location.pathname);
  const createBusiness = currentBusiness && hasBusinessAccess(agent, currentBusiness) ? currentBusiness : firstTaskBusiness(agent) || (hasAdvertisingAccess(agent) ? "advertising" : undefined);
  const createHref = createBusiness === "advertising" ? "/advertising/new" : createBusiness ? `${taskBasePath(createBusiness)}/new` : undefined;
  const currentPage = pageTab(location.pathname);
  const breadcrumbItems = [{ title: <Link to="/">首页</Link> }];
  if (currentBusiness) breadcrumbItems.push({ title: <Link to={currentBusiness === "advertising" ? "/advertising" : taskBasePath(currentBusiness)}>{currentBusiness === "advertising" ? "发布广告" : businessLabels[currentBusiness]}</Link> });
  if (currentPage && location.pathname !== "/") breadcrumbItems.push({ title: <span aria-current="page">{currentPage.label.split(" · ").slice(currentBusiness && currentBusiness !== "advertising" ? 1 : 0).join(" · ")}</span> });
  return (
    <ConfigProvider theme={{ token: { fontSize } }}>
      <ProLayout
        title="体验官"
        logo={<img className="brand-mark small" src="/brand-icon.png" alt="体验官 Logo" />}
        layout="mix"
        fixSiderbar
        fixedHeader
        siderWidth={236}
        location={location}
        menuProps={{ selectedKeys: [selectedNavigation(location.pathname, location.search)] }}
        route={{ routes }}
        menuItemRender={(item, dom) => <Link to={item.path || "/"}>{dom}</Link>}
        menuRender={(props, dom) => !props.isMobile && props.collapsed ? null : dom}
        headerTitleRender={() => <WorkspaceBrand />}
        collapsedButtonRender={false}
        headerContentRender={(props) => <div className="workspace-header-navigation">
          {!props.isMobile && <Button
            type="text"
            className="header-menu-toggle"
            aria-label={props.collapsed ? "展开菜单栏" : "收起菜单栏"}
            title={props.collapsed ? "展开菜单栏" : "收起菜单栏"}
            aria-expanded={!props.collapsed}
            icon={props.collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => props.onCollapse?.(!props.collapsed)}
          />}
          <Breadcrumb className="workspace-breadcrumb" items={breadcrumbItems} />
        </div>}
        menuHeaderRender={false}
        avatarProps={{
          src: undefined,
          title: agent.name,
          render: () => (
            <Dropdown
              trigger={["click"]}
              menu={{
                items: [
                  {
                    key: "account",
                    icon: <SettingOutlined />,
                    label: "账号与安全",
                    onClick: () => navigate("/account"),
                  },
                  {
                    key: "logout",
                    icon: <LogoutOutlined />,
                    label: "退出登录",
                    onClick: () => void logout(),
                  },
                ],
              }}
            >
              <button type="button" className="header-avatar" aria-label={`账号菜单：${agent.name}`} title={agent.name}>
                <Avatar
                  style={{ background: "#e8efff", color: "#3561ad" }}
                  size={32}
                >
                  {agent.name.slice(0, 1)}
                </Avatar>
                <DownOutlined className="header-avatar-chevron" />
              </button>
            </Dropdown>
          ),
        }}
        actionsRender={() => [
          <HeaderTools key="tools" routes={routes} fontSize={fontSize} onFontSize={setFontSize} />,
          <Button
            key="notify"
            className="header-notification"
            type="text"
            aria-label={notifications.count == null ? "消息中心" : `消息中心，${notifications.count} 条未读`}
            icon={
              <Badge count={notifications.count} size="small" overflowCount={99} offset={[5, -3]}>
                <BellOutlined />
              </Badge>
            }
            onClick={() => navigate("/notifications")}
          />,
        ]}
        token={{
          header: {
            colorBgHeader: "#fff",
            colorHeaderTitle: "#59616d",
            colorTextMenu: "#59616d",
            colorTextMenuSecondary: "#59616d",
            colorTextMenuSelected: "#fff",
            colorTextMenuActive: "#fff",
            colorBgMenuItemHover: "#254875",
            heightLayoutHeader: 42,
          },
          sider: {
            paddingInlineLayoutMenu: 0,
            paddingBlockLayoutMenu: 0,
            colorMenuBackground: "#304156",
            colorTextMenu: "#a9b8ce",
            colorTextMenuSelected: "#fff",
            colorBgMenuItemSelected: "#254875",
            colorBgMenuItemHover: "#1d3555",
            colorTextMenuItemHover: "#fff",
            colorTextMenuActive: "#fff",
          },
          pageContainer: {
            paddingBlockPageContainerContent: 24,
            paddingInlinePageContainerContent: 32,
          },
        }}
        menuFooterRender={() => (
          <div className="sidebar-footer">
            <div>
              <SafetyCertificateOutlined /> 数据安全保护中
            </div>
            <small>专属空间 · 仅查看所属业务</small>
          </div>
        )}
      >
        <PageTabs key={agent.id} agent={agent} />
        {agent.business_frozen && (
          <div className="policy-banner">
            当前业务已冻结，任务创建与提交暂不可用。请联系平台管理员了解详情。
          </div>
        )}
        {agent.publish_restricted && !agent.business_frozen && (
          <div className="policy-banner">
            当前账号发布权限受限，可查看数据与保存任务草稿。
          </div>
        )}
        <PageContainer title={false} breadcrumb={{ items: [] }}>
          <Routes>
            <Route path="/" element={<Dashboard agent={agent} />} />
            {taskBusinesses.map((business) => <Route key={business} path={`/${business}`}>
              <Route index element={<Navigate to={taskBasePath(business)} replace />} />
              <Route path="tasks" element={<BusinessAccess key={`${business}-tasks`} agent={agent} business={business}><Tasks agent={agent} business={business} /></BusinessAccess>} />
              <Route path="tasks/new" element={<BusinessAccess key={`${business}-new`} agent={agent} business={business}><TaskEditor agent={agent} business={business} /></BusinessAccess>} />
              <Route path="tasks/:id/edit" element={<BusinessAccess key={`${business}-edit-${location.pathname}`} agent={agent} business={business}><TaskEditor agent={agent} business={business} /></BusinessAccess>} />
              <Route path="tasks/:id" element={<BusinessAccess key={`${business}-detail-${location.pathname}`} agent={agent} business={business}><TaskDetail agent={agent} business={business} /></BusinessAccess>} />
              <Route path="submissions" element={<BusinessAccess key={`${business}-submissions`} agent={agent} business={business}><SubmissionsCenter business={business} /></BusinessAccess>} />
              <Route path="reports" element={<BusinessAccess key={`${business}-reports`} agent={agent} business={business}><Reports business={business} /></BusinessAccess>} />
            </Route>)}
            <Route path="/promotion/materials" element={<BusinessAccess key="promotion-materials" agent={agent} business="promotion"><MaterialsPage disabled={agent.business_frozen} /></BusinessAccess>} />
            <Route path="/tasks" element={<LegacyBusinessRedirect agent={agent} />} />
            <Route path="/tasks/new" element={<LegacyBusinessRedirect agent={agent} />} />
            <Route path="/tasks/:id" element={<LegacyTaskRedirect agent={agent} />} />
            <Route path="/tasks/:id/edit" element={<LegacyTaskRedirect agent={agent} />} />
            <Route path="/reports" element={<LegacyBusinessRedirect agent={agent} />} />
            <Route path="/submissions" element={<LegacyBusinessRedirect agent={agent} />} />
            <Route path="/materials" element={<LegacyBusinessRedirect agent={agent} />} />
            <Route path="/advertising" element={<AdvertisingAccess key="ads" agent={agent}><Advertising agent={agent} /></AdvertisingAccess>} />
            <Route path="/advertising/new" element={<AdvertisingAccess key="ad-new" agent={agent}><AdvertisingCreate agent={agent} /></AdvertisingAccess>} />
            <Route path="/advertising/materials" element={<AdvertisingAccess key="ad-materials" agent={agent}><AdvertisingMaterials disabled={agent.business_frozen} /></AdvertisingAccess>} />
            <Route path="/advertising/reports" element={<AdvertisingAccess key="ad-reports" agent={agent}><AdvertisingReports /></AdvertisingAccess>} />
            <Route path="/finance" element={<FinancePage />} />
            <Route path="/notifications" element={<Notifications unreadCount={notifications.count} />} />
            <Route path="/help" element={<HelpCenter />} />
            <Route path="/help/feedback" element={<FeedbackList />} />
            <Route path="/help/feedback/new" element={<FeedbackCreate />} />
            <Route path="/help/feedback/:id" element={<FeedbackDetail />} />
            <Route path="/feedback" element={<Navigate to={`/help/feedback${new URLSearchParams(location.search).get("create") === "1" ? "/new" : ""}${location.search}`} replace />} />
            <Route path="/feedback/new" element={<Navigate to={`/help/feedback/new${location.search}`} replace />} />
            <Route path="/feedback/:id" element={<FeedbackDetail />} />
            <Route
              path="/account"
              element={
                <Account
                  agent={agent}
                  onPasswordDone={() => {
                    setAgent(null);
                    navigate("/");
                  }}
                />
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <footer className="page-footer">
            <span>体验官 · 商户协作平台</span>
            <span>
              <TeamOutlined /> 北京时间 UTC+8
            </span>
          </footer>
        </PageContainer>
        {createHref && !agent.business_frozen && <Link className="floating-create" to={createHref} aria-label={createBusiness === "advertising" ? "新建广告" : `新建${businessLabels[createBusiness!]}任务`}><PlusOutlined /></Link>}
      </ProLayout>
    </ConfigProvider>
  );
}

function LegacyBusinessRedirect({ agent }: { agent: Agent }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const taskId = params.get("task_id");
  if (taskId && ["/reports", "/submissions"].includes(location.pathname)) return <LegacyTaskRedirect agent={agent} taskId={taskId} destination={location.pathname} />;
  const preferred = params.get("task_type") === "message_send" ? "sms" : undefined;
  const business = preferred && hasBusinessAccess(agent, preferred) ? preferred : firstTaskBusiness(agent);
  const to = location.pathname === "/materials"
    ? hasBusinessAccess(agent, "promotion") ? "/promotion/materials" : defaultBusinessPath(agent)
    : business ? `/${business}${location.pathname}` : defaultBusinessPath(agent);
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
}

/** Notification and old bookmarks resolve their real task business before redirecting. */
function LegacyTaskRedirect({ agent, taskId, destination }: { agent: Agent; taskId?: string; destination?: string }) {
  const params = useParams();
  const id = taskId || params.id;
  const location = useLocation();
  const [task, setTask] = useState<Task>();
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setTask(undefined); setError("");
    if (!firstTaskBusiness(agent)) return;
    api<Task>(`/tasks/${id}`).then((value) => { if (active) setTask(value); }).catch((e) => { if (active) setError((e as Error).message); });
    return () => { active = false; };
  }, [id, agent.id, agent.allowed_task_types?.join(","), attempt]);
  if (!firstTaskBusiness(agent)) return <Navigate to={defaultBusinessPath(agent)} replace />;
  if (error) return <Result status="error" title="暂时无法打开任务" subTitle={error} extra={<Button onClick={() => setAttempt((v) => v + 1)}>重试</Button>} />;
  if (!task) return <Spin tip="正在确认任务所属业务…"><div style={{ minHeight: 180 }} /></Spin>;
  const business = taskRouteBusiness(task, destination);
  if (!hasBusinessAccess(agent, business)) return <Result status="403" title={`${businessLabels[business]}权限未开通`} subTitle="请联系平台管理员。" />;
  return <Navigate to={`${destination ? `/${business}${destination}` : taskPath(business, task.id, location.pathname.endsWith("/edit"))}${location.search}${location.hash}`} replace />;
}
